/**
 * Engage-specific Axios instance.
 *
 * A fresh instance (not the shared @ayla/api singleton) so Engage-specific
 * interceptors (MFA 403 handling) are isolated and never affect other apps.
 */
import axios, { isAxiosError, type InternalAxiosRequestConfig } from "axios";
import { getApiBaseUrl } from "@ayla/config";
import { MFA_DETAIL_VERIFY, MFA_DETAIL_ENROLL } from "@ayla/auth";
import { useAuthStore } from "@/state/authStore";

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  withCredentials: true,
});

// CSRF request interceptor — mirrors the @ayla/api baseline
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (config.method && UNSAFE_METHODS.has(config.method.toUpperCase())) {
    const csrfToken = getCsrfToken();
    if (csrfToken) config.headers.set("X-CSRF-Token", csrfToken);
  }
  return config;
});

// Engage-specific: catch mid-session MFA 403s and set store flags
apiClient.interceptors.response.use(
  response => response,
  error => {
    if (isAxiosError(error) && error.response?.status === 403) {
      const detail: string = error.response?.data?.detail ?? "";
      if (detail === MFA_DETAIL_VERIFY) {
        useAuthStore.setState({ mfaVerificationRequired: true });
      } else if (detail === MFA_DETAIL_ENROLL) {
        useAuthStore.setState({ mfaEnrollmentRequired: true });
      }
    }
    return Promise.reject(error);
  }
);

function getCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const meta = document.querySelector('meta[name="csrf-token"]');
  if (meta) return meta.getAttribute("content");
  for (const cookie of document.cookie.split(";")) {
    const [name, value] = cookie.trim().split("=");
    if (name === "csrftoken" || name === "XSRF-TOKEN")
      return decodeURIComponent(value);
  }
  return null;
}
